import {
  AspectRatio,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { CatalogType } from "../../../types/catalog";
import {
  LockOpenOutlined,
  LockOutlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { theme } from "../../../components/Theme";
import { If, IfElse } from "../../../components/Condition";
import { useState } from "react";
import dayjs from "dayjs";
import { CatalogService } from "../../../services/catalog.service";
import { useTranslation } from "react-i18next";
import { InventoryService } from "../../../services/inventory.service";
import { useAuthStore } from "../../../store/session";
import { CartDataType } from "../../../types/cart";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { SxProps } from "@mui/material";

type Props = {
  catalog: CatalogType;
  withCatalogType?: boolean;
  withCart?: boolean;
  cartItems?: CartDataType;
  isSellerView?: boolean;
  size?: string;
  containerDirection?: {
    xs?: "column" | "row" | "row-reverse" | "column-reverse";
    sm?: "column" | "row" | "row-reverse" | "column-reverse";
    md?: "column" | "row" | "row-reverse" | "column-reverse";
    lg?: "column" | "row" | "row-reverse" | "column-reverse";
    xl?: "column" | "row" | "row-reverse" | "column-reverse";
  };
  containerStyle?: SxProps;
  buttonContainerStyle?: SxProps;
};

const CatalogueHeader = ({
  catalog,
  withCart = false,
  cartItems,
  withCatalogType = true,
  isSellerView = false,
  size = "lg",
  containerDirection,
  containerStyle,
  buttonContainerStyle,
}: Props) => {
  const { t } = useTranslation();

  const [isEditCatalogName, setIsEditCatalogName] = useState(false);
  const [errorUpdateName, setErrorUpdateName] = useState<string | undefined>();

  /**
   * update catalog name
   * @param value string
   * @returns
   */
  const handleUpdateName = async (value: string) => {
    if (value === catalog.catalogName) {
      setIsEditCatalogName(false);
      return;
    }

    setErrorUpdateName(undefined);

    try {
      const isDuplicate = await CatalogService.validateCatalogName(value);
      if (isDuplicate) {
        setErrorUpdateName(t("catalog.duplicateCatalogError"));
        return;
      }

      if (catalog) {
        await InventoryService.patchCatalog({
          catalog: {
            id: catalog.id,
            catalogType: catalog.catalogType,
            catalogName: value,
            business: catalog.business.id,
            hidePrice: catalog.hidePrice,
          },
        });

        // setCatalog(updateCatalogResponse);
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to update catalog name:", error);
      setErrorUpdateName(t("catalog.updateCatalogError"));
    } finally {
      setIsEditCatalogName(false);
    }
  };

  return (
    <Stack
      direction={containerDirection ?? { xs: "column", md: "row" }}
      sx={{
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        gap: "16px",
        position: "relative",
        ...containerStyle,
      }}
    >
      <Stack direction={"row"} gap={{ xs: "8px", md: "16px" }}>
        {/* company image */}
        <Box width={"60px"}>
          <AspectRatio
            ratio={1}
            sx={{
              width: size === "lg" ? "60px" : "32px",
              height: size === "lg" ? "60px" : "32px",
              objectFit: "cover",
            }}
          >
            <img
              // src={
              //   remoteImage
              //     ? URL.createObjectURL(remoteImage!)
              //     : "/default-company-image.png"
              // }
              src={
                import.meta.env.VITE_API_URL + "/objects/" + catalog.business.id
              }
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "/default-company-image.png";
              }}
              alt="company-logo"
            />
          </AspectRatio>
        </Box>

        {/* catalogue name & company name */}
        <Stack
          direction={"column"}
          sx={{
            justifyContent: "center",
          }}
        >
          <IfElse
            condition={!isSellerView}
            ifBlock={
              <>
                <Typography
                  level={size === "lg" ? "text-md-medium" : "text-xs-medium"}
                  textColor={theme.palette.common.black}
                >
                  {catalog?.business?.companyName}
                </Typography>

                <Typography
                  level={size === "lg" ? "text-lg-bold" : "text-xs-bold"}
                  textColor={theme.palette.common.black}
                >
                  {catalog?.catalogName}
                </Typography>
              </>
            }
            elseBlock={
              <>
                <Stack direction={"row"} gap={"8px"}>
                  {/* active chip */}
                  {dayjs().isAfter(dayjs(catalog.periodEndDate)) ? (
                    <Chip
                      color="neutral"
                      sx={{
                        borderRadius: "4px",
                        paddingY: "4px",
                        paddingX: "8px",
                        backgroundColor: theme.palette.neutral[400],
                      }}
                    >
                      <Typography
                        level="text-xs-semibold"
                        textColor={theme.palette.common.white}
                      >
                        Expired
                      </Typography>
                    </Chip>
                  ) : (
                    <Chip
                      color="success"
                      sx={{
                        borderRadius: "4px",
                        paddingY: "4px",
                        paddingX: "8px",
                        backgroundColor: theme.palette.others.active_chip_bg,
                      }}
                    >
                      <Typography level="text-xs-semibold">Active</Typography>
                    </Chip>
                  )}

                  {/* private catalog chip */}
                  <If condition={catalog.catalogType === "PRIVATE"}>
                    <Chip
                      color="success"
                      sx={{
                        borderRadius: "4px",
                        paddingY: "4px",
                        paddingX: "8px",
                        backgroundColor: theme.palette.others.active_chip_bg,
                      }}
                    >
                      <Stack direction={"row"} gap={"4px"}>
                        <LockClosedIcon width={16} height={16} />
                        <Typography level="text-xs-semibold">
                          {t("catalog.catalogType.pinProtected")}
                        </Typography>
                      </Stack>
                    </Chip>
                  </If>
                </Stack>

                {/* editable catalogName */}
                {!isEditCatalogName ? (
                  <Typography
                    level={"text-xl2-bold"}
                    textColor={theme.palette.common.black}
                    onClick={() => setIsEditCatalogName(true)}
                  >
                    {catalog?.catalogName}
                  </Typography>
                ) : (
                  <FormControl>
                    <Input
                      placeholder={t(
                        "catalog.inventory.createCatalog.placeholder"
                      )}
                      defaultValue={catalog?.catalogName}
                      type="text"
                      fullWidth
                      autoFocus
                      onBlur={(e) => {
                        handleUpdateName(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleUpdateName(e.currentTarget.value);
                        }
                      }}
                    />

                    {errorUpdateName && (
                      <FormHelperText>{errorUpdateName}</FormHelperText>
                    )}
                  </FormControl>
                )}
              </>
            }
          />
        </Stack>
      </Stack>

      {/* <Divider
        sx={{
          display: { xs: "block", md: "none" },
          width: "calc(100% + 2rem)",
          position: "absolute",
          bottom: withCatalogType && withCart ? "42.5%" : 0,
          left: -16,
        }}
      /> */}

      {/* catalogType and cart button */}
      <Stack
        direction={"row"}
        sx={{
          justifyContent: "space-between",
          width: { xs: "100%", lg: "auto" },
          gap: "8px",
          alignItems: "center",
          ...buttonContainerStyle,
        }}
      >
        <If condition={withCatalogType}>
          <CatalogTypeButton catalogType={catalog.catalogType} />
        </If>

        <If condition={withCart}>
          <CartButton catalog={catalog} cartItems={cartItems} />
        </If>
      </Stack>
    </Stack>
  );
};

const CatalogTypeButton = ({ catalogType }: { catalogType: string }) => {
  const { t } = useTranslation();

  return (
    <Button
      onClick={() => {}}
      sx={{
        backgroundColor: theme.palette.others.active_chip_bg,
        color: theme.palette.primary[900],
        "&:hover": {
          backgroundColor: theme.palette.others.active_chip_bg,
        },
      }}
      startDecorator={
        catalogType === "PUBLIC" ? (
          <LockOpenOutlined
            style={{
              width: 18,
              height: 18,
              marginRight: "0.5rem",
            }}
          />
        ) : (
          <LockOutlined
            style={{
              color: theme.palette.primary[900],
              width: 18,
              height: 18,
              marginRight: "0.5rem",
            }}
          />
        )
      }
      className="capitalize"
    >
      <Typography level="text-sm-semibold">
        {t(
          `catalog.catalogType.${
            catalogType === "PUBLIC" ? "public" : "pinProtected"
          }`
        )}
      </Typography>
    </Button>
  );
};

export const CartButton = ({
  cartItems,
}: {
  catalog: CatalogType;
  cartItems?: CartDataType;
}) => {
  const { t } = useTranslation();
  const { catalogId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const { session } = useAuthStore();

  const isGuestMode = window.location.pathname.startsWith("/public");

  return (
    <Link
      // to={
      //   isGuestMode
      //     ? catalog?.catalogType === "PRIVATE"
      //       ? `/client/suppliers/${catalogId}/items`
      //       : `/public/cart/${catalogId}/items`
      //     : orderId
      //     ? `/client/orders/${orderId}`
      //     : session?.accessToken
      //     ? `/client/cart/${catalogId}/items`
      //     : `/public/cart/${catalogId}/items`
      // }
      to={
        isGuestMode
          ? `/public/cart/${catalogId}/items`
          : orderId
          ? `/client/orders/${orderId}`
          : session?.accessToken
          ? `/client/cart/${catalogId}/items`
          : `/public/cart/${catalogId}/items`
      }
    >
      <Button
        variant="linkz-outlined-primary"
        startDecorator={<ShoppingCartOutlined width={"18px"} height={"18px"} />}
        sx={{
          ml: { xs: "auto !important", lg: 0 },
        }}
      >
        {t(`catalog.${orderId ? "order" : "cart"}`)}{" "}
        {!orderId && cartItems
          ? cartItems.items.length > 0 && `(${cartItems.items.length})`
          : ""}
      </Button>
    </Link>
  );
};

export default CatalogueHeader;
